import React, {forwardRef} from 'react';
import {Loader} from 'lucide-react';

// The backoffice button set, macOS / iOS style on the teal theme:
//   primary   — filled teal, the one main action of a screen (Enregistrer, Nouveau)
//   secondary — white with a hairline border (Import, Exporter, Annuler)
//   tinted    — soft teal fill, secondary action that stays on-brand (Imprimer)
//   ghost     — text only, low-emphasis (Retour, Effacer)
//   plain     — teal text + thin icon, no border: every page / form header action (Import, Nouveau, Enregistrer)
//   danger    — soft red fill, destructive action (Supprimer)
//   dangerSolid / warningSolid — filled, for the confirm button of a dialog
//   warning   — soft amber fill (Convertir…)
// Every button has the same height per size, rounded-xl corners and a subtle
// press effect. `as={Link}` renders a router link with the same look.

const VARIANTS = {
    primary: 'bg-primary text-white shadow-sm shadow-primary/30 hover:bg-primary-dark focus-visible:ring-primary/40',
    secondary: 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-700 shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700 focus-visible:ring-primary/30',
    tinted: 'bg-primary/10 dark:bg-primary/20 text-primary-dark dark:text-primary hover:bg-primary/20 dark:hover:bg-primary/30 focus-visible:ring-primary/30',
    plain: 'text-primary hover:bg-primary/10 focus-visible:ring-primary/30',
    ghost: 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white focus-visible:ring-primary/30',
    danger: 'bg-red-500/10 text-red-600 dark:text-red-400 hover:bg-red-500/15 dark:hover:bg-red-500/20 focus-visible:ring-red-500/30',
    dangerSolid: 'bg-red-600 text-white shadow-sm shadow-red-600/30 hover:bg-red-700 focus-visible:ring-red-500/40',
    warning: 'bg-amber-500/10 text-amber-700 dark:text-amber-400 hover:bg-amber-500/20 focus-visible:ring-amber-500/30',
    warningSolid: 'bg-amber-500 text-white shadow-sm shadow-amber-500/30 hover:bg-amber-600 focus-visible:ring-amber-500/40',
};

const SIZES = {
    sm: {base: 'h-8 px-3 text-sm gap-1.5', icon: 'h-8 w-8', glyph: 'w-4 h-4'},
    md: {base: 'h-10 px-4 text-sm gap-2', icon: 'h-10 w-10', glyph: 'w-4 h-4'},
};

export const buttonClass = ({variant = 'secondary', size = 'md', iconOnly = false, fullWidth = false, className = ''} = {}) =>
    `inline-flex items-center justify-center flex-shrink-0 whitespace-nowrap rounded-xl ${variant === 'plain' ? 'font-normal' : 'font-medium'} select-none
     transition-all duration-150 active:scale-[0.97] focus:outline-none focus-visible:ring-4
     disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100 disabled:shadow-none
     ${VARIANTS[variant] || VARIANTS.secondary} ${iconOnly ? SIZES[size].icon : SIZES[size].base} ${fullWidth ? 'w-full' : ''} ${className}`;

const Button = forwardRef(({
                               as: Component = 'button',
                               variant = 'secondary',
                               size = 'md',
                               icon: Icon,
                               iconRight: IconRight,
                               loading = false,
                               fullWidth = false,
                               // Hide the label below sm: (icon stays, label goes to title/aria-label)
                               compact = false,
                               className = '',
                               children,
                               disabled,
                               type,
                               title,
                               ...props
                           }, ref) => {
    const iconOnly = !children;
    // Plain header actions: slightly larger, thin-stroke icons
    const plain = variant === 'plain';
    const glyph = plain ? 'w-5 h-5' : SIZES[size].glyph;
    const stroke = plain ? 1.5 : undefined;
    const label = typeof children === 'string' ? children : undefined;
    return (
        <Component
            ref={ref}
            type={Component === 'button' ? (type || 'button') : type}
            disabled={Component === 'button' ? (disabled || loading) : undefined}
            aria-disabled={Component !== 'button' && disabled ? true : undefined}
            title={title || (compact || iconOnly ? label : undefined)}
            aria-label={iconOnly ? title : (compact ? label : undefined)}
            className={buttonClass({
                variant, size, fullWidth,
                iconOnly,
                className: `${compact ? 'max-sm:w-10 max-sm:px-0' : ''} ${className}`,
            })}
            {...props}
        >
            {loading
                ? <Loader className={`${glyph} animate-spin`} aria-hidden="true"/>
                : Icon && <Icon className={glyph} strokeWidth={stroke} aria-hidden="true"/>}
            {children && <span className={`${compact ? 'max-sm:sr-only' : ''} ${plain ? 'text-[15px]' : ''}`}>{children}</span>}
            {IconRight && <IconRight className={`${glyph} ${compact ? 'max-sm:hidden' : ''}`} strokeWidth={stroke} aria-hidden="true"/>}
        </Component>
    );
});

Button.displayName = 'Button';

export default Button;
